/**
 * Flow Builder page for DAG-based automation rules.
 * Routes: /app/automations/new/flow, /app/automations/:id/flow
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { FlowCanvas } from '@/components/automations/FlowCanvas';
import { TriggerEditorSheet } from '@/components/automations/TriggerEditorSheet';
import { ActionEditorSheet } from '@/components/automations/ActionEditorSheet';
import { ConditionEditorSheet } from '@/components/automations/ConditionEditorSheet';
import { useApp } from '@/contexts/AppContext';
import {
  useAutomationRules,
  useCreateAutomationRule,
  useUpdateAutomationRule,
  type AutomationRuleInsert,
} from '@/hooks/useAutomationRules';
import type {
  DagDefinition,
  DagNode,
  DagNodeType,
  TriggerNodeConfig,
  ConditionGroupConfig,
  ActionBlockConfig,
  ActionStep,
} from '@/lib/dag-types';
import { toast } from 'sonner';

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function createEmptyDag(): DagDefinition {
  const triggerId = `trigger-${generateId()}`;
  const actionId = `action-${generateId()}`;
  const endId = `end-${generateId()}`;

  return {
    nodes: [
      {
        id: triggerId,
        type: 'trigger',
        config: {
          title: 'New Trigger',
          trigger_type: 'system_offline',
          trigger_config: {},
          scope_type: 'all',
        } as TriggerNodeConfig,
      },
      {
        id: actionId,
        type: 'action_block',
        config: {
          title: 'Actions',
          steps: [
            {
              id: `step-${generateId()}`,
              type: 'action',
              action_type: 'notification',
              action_config: { channels: ['in_app', 'email'], recipients: 'installer_only' },
              title: 'Send Notification',
              subtitle: 'Channel: Email + In-App',
            },
          ],
        } as ActionBlockConfig,
      },
      {
        id: endId,
        type: 'end',
        config: {},
      },
    ],
    edges: [
      { from: triggerId, to: actionId },
      { from: actionId, to: endId },
    ],
  };
}

export default function AutomationFlowBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { organization } = useApp();
  const { rules, isLoading: rulesLoading } = useAutomationRules();
  const createRule = useCreateAutomationRule();
  const updateRule = useUpdateAutomationRule();

  const isEdit = Boolean(id);
  const existingRule = isEdit ? rules.find((r) => r.id === id) : null;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [dag, setDag] = useState<DagDefinition>(createEmptyDag());
  const [saving, setSaving] = useState(false);
  const [editingNode, setEditingNode] = useState<{ id: string; type: DagNodeType } | null>(null);

  // Load existing rule
  useEffect(() => {
    if (isEdit && existingRule) {
      setName(existingRule.name);
      setDescription(existingRule.description ?? '');
      setIsEnabled(existingRule.is_enabled);
      if (existingRule.dag_definition) {
        setDag(existingRule.dag_definition);
      }
    }
  }, [isEdit, existingRule]);

  const handleInsertNode = useCallback(
    (afterNodeId: string, type: 'condition_group' | 'action_block') => {
      setDag((prev) => {
        const newId = `${type}-${generateId()}`;
        let newNode: DagNode;

        if (type === 'condition_group') {
          newNode = {
            id: newId,
            type: 'condition_group',
            config: {
              title: 'New Condition',
              match_mode: 'first_match',
              branches: [
                {
                  label: 'Branch A',
                  conditions: [],
                  then_steps: [
                    {
                      id: `step-${generateId()}`,
                      type: 'action',
                      action_type: 'notification',
                      action_config: {},
                      title: 'New Action',
                    },
                  ],
                },
              ],
            } as ConditionGroupConfig,
          };
        } else {
          newNode = {
            id: newId,
            type: 'action_block',
            config: {
              title: 'New Action Block',
              steps: [
                {
                  id: `step-${generateId()}`,
                  type: 'action',
                  action_type: 'notification',
                  action_config: {},
                  title: 'New Action',
                },
              ],
            } as ActionBlockConfig,
          };
        }

        // Insert node: find edges from afterNodeId, splice in new node
        const newEdges = prev.edges.flatMap((edge) => {
          if (edge.from === afterNodeId) {
            return [
              { from: afterNodeId, to: newId },
              { from: newId, to: edge.to },
            ];
          }
          return [edge];
        });

        return {
          nodes: [...prev.nodes, newNode],
          edges: newEdges,
        };
      });
    },
    []
  );

  const handleNodeClick = useCallback((nodeId: string) => {
    const node = dag.nodes.find((n) => n.id === nodeId);
    if (!node || node.type === 'end') return;
    setEditingNode({ id: nodeId, type: node.type });
  }, [dag.nodes]);

  const handleUpdateNodeConfig = useCallback(
    (nodeId: string, config: TriggerNodeConfig | ConditionGroupConfig | ActionBlockConfig) => {
      setDag((prev) => ({
        ...prev,
        nodes: prev.nodes.map((n) => (n.id === nodeId ? { ...n, config } : n)),
      }));
    },
    []
  );

  const handleDeleteNode = useCallback((nodeId: string) => {
    setDag((prev) => {
      const node = prev.nodes.find((n) => n.id === nodeId);
      if (!node || node.type === 'trigger' || node.type === 'end') return prev;

      // Find parents and children of the deleted node
      const parents = prev.edges.filter((e) => e.to === nodeId).map((e) => e.from);
      const children = prev.edges.filter((e) => e.from === nodeId).map((e) => e.to);

      // Remove edges involving this node
      const remainingEdges = prev.edges.filter((e) => e.from !== nodeId && e.to !== nodeId);

      // Reconnect parents to children
      const newEdges = [...remainingEdges];
      for (const parent of parents) {
        for (const child of children) {
          newEdges.push({ from: parent, to: child });
        }
      }

      return {
        nodes: prev.nodes.filter((n) => n.id !== nodeId),
        edges: newEdges,
      };
    });
    setEditingNode(null);
  }, []);

  // Get current trigger type for condition field resolution
  const currentTriggerType = (() => {
    const triggerNode = dag.nodes.find((n) => n.type === 'trigger');
    return (triggerNode?.config as TriggerNodeConfig | undefined)?.trigger_type ?? 'system_offline';
  })();

  const editingNodeData = editingNode ? dag.nodes.find((n) => n.id === editingNode.id) : null;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Extract trigger config for the rule-level fields
    const triggerNode = dag.nodes.find((n) => n.type === 'trigger');
    const triggerConfig = triggerNode?.config as TriggerNodeConfig | undefined;

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateRule.mutateAsync({
          id,
          updates: {
            name: name.trim(),
            description: description.trim() || undefined,
            is_enabled: isEnabled,
            trigger_type: triggerConfig?.trigger_type ?? 'system_offline',
            trigger_config: triggerConfig?.trigger_config ?? {},
            scope_type: triggerConfig?.scope_type ?? 'all',
            scope_property_ids: triggerConfig?.scope_property_ids ?? [],
            scope_tags: triggerConfig?.scope_tags ?? [],
            is_dag: true,
            dag_definition: dag,
          },
        });
        toast.success('Flow updated');
      } else {
        const insert: AutomationRuleInsert = {
          name: name.trim(),
          description: description.trim() || undefined,
          is_enabled: isEnabled,
          trigger_type: triggerConfig?.trigger_type ?? 'system_offline',
          trigger_config: triggerConfig?.trigger_config ?? {},
          scope_type: triggerConfig?.scope_type ?? 'all',
          scope_property_ids: triggerConfig?.scope_property_ids ?? [],
          scope_tags: triggerConfig?.scope_tags ?? [],
          action_type: 'notification',
          action_config: {},
          is_dag: true,
          dag_definition: dag,
        };
        await createRule.mutateAsync(insert);
        toast.success('Flow created');
      }
      navigate('/app/automations');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (rulesLoading && isEdit) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/app/automations">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-wt-text-primary">
              {isEdit ? 'Edit Flow' : 'New Flow'}
            </h1>
            <p className="text-xs text-wt-text-muted">
              DAG-based automation with branching conditions
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
            <span className="text-sm text-wt-text-secondary">
              {isEnabled ? 'Active' : 'Inactive'}
            </span>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Name + description */}
      <div className="grid gap-4 max-w-lg">
        <div className="grid gap-1.5">
          <Label htmlFor="flow-name">Name</Label>
          <Input
            id="flow-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Reserve Breach Response"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="flow-desc">Description (optional)</Label>
          <Input
            id="flow-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this automation do?"
          />
        </div>
      </div>

      {/* Flow canvas */}
      <div className="rounded-lg border border-wt-border-subtle bg-wt-bg-primary p-4">
        <FlowCanvas
          dag={dag}
          onNodeClick={handleNodeClick}
          onInsertNode={handleInsertNode}
        />
      </div>

      {/* Editor sheets */}
      {editingNodeData?.type === 'trigger' && (
        <TriggerEditorSheet
          open={true}
          onClose={() => setEditingNode(null)}
          config={editingNodeData.config as TriggerNodeConfig}
          onSave={(config) => handleUpdateNodeConfig(editingNodeData.id, config)}
        />
      )}
      {editingNodeData?.type === 'action_block' && (
        <ActionEditorSheet
          open={true}
          onClose={() => setEditingNode(null)}
          config={editingNodeData.config as ActionBlockConfig}
          onSave={(config) => handleUpdateNodeConfig(editingNodeData.id, config)}
          onDelete={() => handleDeleteNode(editingNodeData.id)}
        />
      )}
      {editingNodeData?.type === 'condition_group' && (
        <ConditionEditorSheet
          open={true}
          onClose={() => setEditingNode(null)}
          config={editingNodeData.config as ConditionGroupConfig}
          triggerType={currentTriggerType}
          onSave={(config) => handleUpdateNodeConfig(editingNodeData.id, config)}
          onDelete={() => handleDeleteNode(editingNodeData.id)}
        />
      )}
    </div>
  );
}
